import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  CircularProgress,
} from "@mui/material";
import { RxDoubleArrowDown, RxDoubleArrowUp } from "react-icons/rx";
import { FaSort, FaSortUp } from "react-icons/fa";

import { usePapaParse } from "react-papaparse";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { saveAs } from "file-saver";
import DatePicker from "react-datepicker";
import zoomPlugin from "chartjs-plugin-zoom";
import { useMutation } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import CircularLoading from "../../../common/particles.jsx/CircularLoading";
import {
  fetchReportingData,
  getReportingExcel,
  getReportingTable,
} from "../../../../services/ReportingApi";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import LoadingBackdrop from "../../../common/particles.jsx/LoadingBackdrop";
import PagePagination from "../../../common/particles.jsx/PagePagination";
import { useNavigate, useParams } from "react-router-dom";
import ErrorPopup from "../../../common/particles.jsx/ErrorPopup";

const toDate = (date) => (date ? moment(date).format("DD MMM YYYY HH:mm") : "");
const toApiDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const Penjualan = () => {
  const { jsonToCSV } = usePapaParse();
  const { pageSetting } = useParams();
  const navigate = useNavigate();

  const [method, setMethod] = useState(1);
  const [date, setDate] = useState(false);
  const [period, setPeriod] = useState(8);
  const [periodDummy, setPeriodDummy] = useState(8);
  const [rows, setRows] = useState([]);
  const [sortBy, setSortBy] = useState({
    column: ["quantity", "total"],
    order: "asc",
  });

  const [offset, setOffset] = useState(1);
  const [fetchLimit, setFetchLimit] = useState(10);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const navigateTo = (
    newOffset,
    newLimit,
    newStart,
    newEnd,
    newPeriod,
    newMethod,
  ) => {
    navigate(
      `/reporting/offset=${newOffset}&limit=${newLimit}&start_date=${
        newStart ? newStart : ""
      }&end_date=${
        newEnd ? newEnd : ""
      }&period=${newPeriod}&method=${newMethod}`,
    );
  };

  const {
    mutate: mutatePenjualan,
    data: penjualan,
    isLoading: isReportingLoading,
  } = useMutation({
    mutationFn: fetchReportingData,
    onError: (err) => {
      setErrorMessage(err.id);
    },
  });

  const {
    mutate: mutateReportingTable,
    data: reportingTable,
    isLoading: isReportingTableLoading,
  } = useMutation({
    mutationFn: getReportingTable,
  });

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin,
  );

  let offlineData = penjualan?.chart?.offline_order
    ? penjualan?.chart?.offline_order.sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      )
    : [];

  let onlineData = [];

  if (penjualan?.chart?.online_order) {
    onlineData = penjualan?.chart?.online_order.sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );
  }

  const labels = [
    ...new Set([
      ...onlineData.map((item) => moment(item.date).format("DD/MM/YYYY")),
      ...offlineData.map((item) => moment(item.date).format("DD/MM/YYYY")),
    ]),
  ];

  const datas = useMemo(() => {
    return {
      labels: labels,
      datasets: [
        {
          label: "Penjualan Offline",
          data: offlineData.map((item) => item.total_order),
          borderColor: "rgb(255, 99, 132)",
          backgroundColor: "rgba(255, 99, 132, 0.5)",
        },
        {
          label: "Penjualan Online",
          data: onlineData.map((item) => item.total_order),
          borderColor: "rgb(53, 162, 235)",
          backgroundColor: "rgba(53, 162, 235, 0.5)",
        },
      ],
    };
  }, [offlineData, onlineData]);

  const options = {
    mantainAspectRatio: false,
    scales: {
      x: {
        stacked: false,
      },
      y: {
        stacked: false,
      },
    },

    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
      },
      // zoom: {
      //   pan: {
      //     enabled: true,
      //     mode: 'x',
      //   },
      //   zoom: {
      //     pinch: {
      //       enabled: true, // Enable pinch zooming
      //     },
      //     wheel: {
      //       enabled: true, // Enable wheel zooming
      //     },
      //     mode: 'x',
      //   },
      // },
    },
  };

  const options2 = {
    chart: {
      type: "donut",
    },
    series: {
      data: penjualan?.top?.payment?.map((data) => data.total),
    },
    labels: penjualan?.top?.payment?.map((data) => data.name),
  };

  const handleDownload = () => {
    const canvas = document.getElementById("a");
    canvas.toBlob(function (blob) {
      saveAs(blob, "test.png");
    });
  };

  const handleSort = (column) => {
    // setLoading(true);

    setSortBy((prevSortBy) => {
      return {
        ...prevSortBy,
        column: [column],
        order: prevSortBy.order === "asc" ? "desc" : "asc",
      };
    });

    const newSortBy = {
      column: column,
      order: sortBy.order === "asc" ? "desc" : "asc",
    };
    setSortBy(newSortBy);
    const sortedRows = [...rows].sort((a, b) => {
      if (sortBy.order === "asc") {
        if (a[column] < b[column]) {
          return -1;
        }
        if (a[column] > b[column]) {
          return 1;
        }
      } else {
        if (a[column] > b[column]) {
          return -1;
        }
        if (a[column] < b[column]) {
          return 1;
        }
      }
      return 0;
    });
    setRows(sortedRows);
  };

  const handleJsonToXlsxExisting = (csvExisting) => {
    const worksheet = XLSX.utils.json_to_sheet(csvExisting);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "hasil_reporting_OLIN");

    XLSX.utils.sheet_add_aoa(worksheet, [Object.keys(csvExisting[0])], {
      origin: "A1",
    });
    XLSX.writeFile(workbook, "hasil_reporting_OLIN.xlsx");
  };

  const { mutate: mutateExportCsv, isLoading: exportCsvLoading } = useMutation({
    mutationFn: getReportingExcel,
    onSuccess: (data) => handleJsonToXlsxExisting(data),
  });

  const OverviewTable = ({ dataArr, title }) => {
    return (
      <table className="w-full">
        <thead className="border-b-2">
          <th className="text-blue-500 font-bold p-1 w-1/3">{title}</th>
          <th className="text-xs font-semibold text-center p-1">
            Total Penjualan
          </th>
          <th className="text-xs font-semibold text-right p-1">Jumlah</th>
        </thead>
        <tbody>
          {dataArr.map((item, i) => (
            <tr>
              <td className="p-1 w-1/3">{item.name_platform}</td>
              <td className="text-center p-1">{item.total}</td>
              <td className="text-right p-1">
                {toIDR(item.total_price ? item.total_price : 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    mutateReportingTable({
      method,
      period: Number(period) === 10 ? 7 : Number(period),
      limit: fetchLimit,
      offset,
      start_date: startDate ? toApiDate(startDate) : "",
      end_date: endDate ? toApiDate(endDate) : "",
    });
  }, [method, period, offset, fetchLimit, endDate]);

  useEffect(() => {
    mutatePenjualan({
      period: period === 10 ? 7 : period,
      method,
      start_date: startDate ? toApiDate(startDate) : "",
      end_date: endDate ? toApiDate(endDate) : "",
    });
  }, [method, period, endDate]);

  useEffect(() => {
    if (pageSetting) {
      var limitSetting, offsetSetting, startDate, endDate, period, method;

      pageSetting.split("&").map((item) => {
        var val = item.split("=");
        if (val[0] === "limit") limitSetting = val[1];
        if (val[0] === "offset") offsetSetting = val[1];
        if (val[0] === "start_date") startDate = val[1];
        if (val[0] === "end_date") endDate = val[1];
        if (val[0] === "period") period = val[1];
        if (val[0] === "method") method = val[1];
      });

      setOffset(offsetSetting ? Number(offsetSetting) : 1);
      setFetchLimit(limitSetting ? Number(limitSetting) : 50);
      setStartDate(startDate && endDate ? new Date(startDate) : null);
      setEndDate(endDate ? moment(endDate) : null);
      setPeriod(period ? Number(period) : 8);
      setPeriodDummy(period ? Number(period) : 8);
      setMethod(method ? Number(method) : 1);
    }
  }, [pageSetting]);

  return (
    <div className="w-full">
      <LoadingBackdrop isLoading={exportCsvLoading || isReportingLoading} />
      <div className="grid grid-cols-1 lg:grid-cols-2 justify-center gap-3 mt-4">
        <div className="p-3 border rounded-lg">
          <p className="text-blue-500 font-bold white">Penjualan Obat</p>
          <hr />
          <div className="w-full flex items-center gap-4 mt-3">
            <p className="w-1/2">Metode Penjualan</p>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Semua Penjualan
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="category_id"
                label="Tambah Kategori"
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value);
                  navigateTo(
                    1,
                    fetchLimit,
                    startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                    endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                    period,
                    e.target.value,
                  );
                }}
              >
                <MenuItem value={1}>Semua Penjualan</MenuItem>
                <MenuItem value={3}>Order Online</MenuItem>
                <MenuItem value={2}>Order Offline</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="w-full flex items-center gap-4 mt-3">
            <p className="w-1/2">Period Penjualan</p>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Period Penjualan
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="category_id"
                label="Tambah Kategori"
                value={periodDummy}
                onChange={(e) => {
                  setPeriod(e.target.value === 10 ? 7 : Number(e.target.value));
                  setStartDate(null);
                  setEndDate(null);

                  if (e.target.value === 7) {
                    setDate(true);
                    return;
                  } else if (e.target.value !== 10) {
                    navigateTo(
                      1,
                      fetchLimit,
                      "",
                      "",
                      e.target.value === 7 ? 10 : e.target.value,
                      method,
                    );
                    setDate(false);
                  }
                  setPeriodDummy(e.target.value);
                }}
              >
                <MenuItem value={8}>Hari ini</MenuItem>
                <MenuItem value={9}>Kemarin</MenuItem>
                <MenuItem value={1}>Minggu Berjalan</MenuItem>
                <MenuItem value={2}>Minggu Lalu</MenuItem>
                <MenuItem value={3}>Bulan Berjalan</MenuItem>
                <MenuItem value={4}>Bulan Lalu</MenuItem>
                <MenuItem value={5}>Tahun Berjalan</MenuItem>
                <MenuItem value={6}>Tahun Lalu</MenuItem>
                <MenuItem
                  value={7}
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setDate(true);
                  }}
                >
                  Pilih Tanggal
                </MenuItem>
                <MenuItem value={10} disabled sx={{ display: "none" }}>
                  {moment(startDate).format("DD/MM/YYYY") +
                    " - " +
                    moment(endDate).format("DD/MM/YYYY")}
                </MenuItem>
              </Select>
            </FormControl>
          </div>
          <p className="text-center my-4 font-bold text-blue-500 text-[30px]">
            {penjualan?.sales_medicine?.total === null ||
            penjualan?.sales_medicine?.total === undefined
              ? 0
              : penjualan?.sales_medicine?.total.toLocaleString("id-ID", {
                  style: "currency",
                  currency: "IDR",
                })}
          </p>
          <div className="flex flex-row items-center justify-between font-bold">
            <p>Order Online :</p>
            <p>
              {penjualan?.sales_medicine?.total_order_online === null ||
              penjualan?.sales_medicine?.total_order_online === undefined
                ? 0
                : penjualan?.sales_medicine?.total_order_online.toLocaleString(
                    "id-ID",
                    {
                      style: "currency",
                      currency: "IDR",
                    },
                  )}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between font-bold mt-4">
            <p>Order Offline :</p>
            <p>
              {penjualan?.sales_medicine?.total_order_offline === null ||
              penjualan?.sales_medicine?.total_order_offline === undefined
                ? 0
                : penjualan?.sales_medicine?.total_order_offline.toLocaleString(
                    "id-ID",
                    {
                      style: "currency",
                      currency: "IDR",
                    },
                  )}
            </p>
          </div>
        </div>

        <div className="p-3 border rounded-lg">
          <div className="w-full">
            <OverviewTable
              title="Online Order"
              dataArr={
                penjualan && !isReportingLoading
                  ? penjualan.sales_total?.online_order
                  : []
              }
            />
          </div>

          <div className="mt-8">
            <OverviewTable
              title="Offline Order"
              dataArr={[
                {
                  name_platform: "On-Site",
                  total: penjualan?.sales_total?.offline_order
                    ? penjualan?.sales_total?.offline_order
                    : 0,
                  total_price: penjualan?.sales_total?.total_price_offline
                    ? penjualan?.sales_total?.total_price_offline
                    : 0,
                },
                {
                  name_platform: "Invoice",
                  total: penjualan?.sales_total?.invoice_order
                    ? penjualan?.sales_total?.invoice_order
                    : 0,
                  total_price: penjualan?.sales_total?.total_price_invoice
                    ? penjualan?.sales_total?.total_price_invoice
                    : 0,
                },
              ]}
            />
          </div>

          <div className="mt-8">
            <OverviewTable
              title="Void/Retur"
              dataArr={[
                {
                  name_platform: "Void/Retur",
                  total: penjualan?.sales_total?.void_order
                    ? penjualan?.sales_total?.void_order
                    : 0,
                  total_price: penjualan?.sales_total?.total_price_void
                    ? penjualan?.sales_total?.total_price_void
                    : 0,
                },
              ]}
            />
          </div>

          <div className="mt-8">
            <OverviewTable
              title="Diskon"
              dataArr={[
                {
                  name_platform: "Diskon",
                  total: penjualan?.sales_total?.total_discount
                    ? penjualan?.sales_total?.total_discount
                    : 0,
                  total_price: penjualan?.sales_total?.total_price_invoice
                    ? penjualan?.sales_total?.total_price_invoice
                    : 0,
                },
              ]}
            />
          </div>

          <p className="text-end mt-5 text-blue-500 font-medium hover:underline cursor-pointer">
            <a href="#drop1">Lihat Detail</a>
          </p>
        </div>
      </div>
      <>
        {penjualan && penjualan !== undefined ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-5 justify-center gap-3 mt-3 ">
              <div className="p-3 border rounded-lg">
                <p>Pendapatan</p>
                <p className="mt-4 font-bold text-[25px]">
                  {penjualan?.total_income?.total === null
                    ? 0
                    : penjualan?.total_income?.total.toLocaleString("id-ID", {
                        style: "currency",
                        currency: "IDR",
                      })}
                </p>

                <div className="flex flex-wrap gap-3 mt-2">
                  {penjualan?.total_income?.decreasing >
                  penjualan?.total_income?.increasing ? (
                    <div className="flex flex-row items-center ">
                      <RxDoubleArrowDown className="text-red-500" />
                      <p>{penjualan?.total_income?.decreasing}% Penurunan</p>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center ">
                      <RxDoubleArrowUp className="text-green-500" />
                      <p>{penjualan?.total_income?.increasing}% Peningkatan</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 border rounded-lg">
                <p>Rata - rata per transaksi</p>
                <p className="mt-4 font-bold text-[25px]">
                  Rp{" "}
                  {penjualan?.average_transaction?.total === null
                    ? 0
                    : penjualan?.average_transaction?.total.toLocaleString(
                        "id-ID",
                        {
                          useGrouping: true,
                        },
                      )}
                </p>

                <div className="flex flex-wrap gap-3 mt-2">
                  {penjualan?.average_transaction?.decreasing >
                  penjualan?.average_transaction?.increasing ? (
                    <div className="flex flex-row items-center ">
                      <RxDoubleArrowDown className="text-red-500" />
                      <p>
                        {penjualan?.average_transaction?.decreasing}% Penurunan
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center ">
                      <RxDoubleArrowUp className="text-green-500" />
                      <p>
                        {String(
                          penjualan?.average_transaction?.increasing,
                        ).slice(0, 5)}
                        % Peningkatan
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 border rounded-lg">
                <p>Transaksi</p>
                <p className="mt-4 font-bold text-[25px]">{`${
                  penjualan?.count_transaction?.total === undefined
                    ? "Tidak ada Transaksi"
                    : penjualan?.count_transaction?.total.toLocaleString(
                        "id-ID",
                        {
                          useGrouping: true,
                        },
                      )
                }`}</p>

                <div className="flex flex-wrap gap-3 mt-2">
                  {penjualan?.count_transaction?.decreasing >
                  penjualan?.count_transaction?.increasing ? (
                    <div className="flex flex-row items-center ">
                      <RxDoubleArrowDown className="text-red-500" />
                      <p>
                        {penjualan?.count_transaction?.decreasing}% Penurunan
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center ">
                      <RxDoubleArrowUp className="text-green-500" />
                      <p>
                        {penjualan?.count_transaction?.increasing}% Peningkatan
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 border rounded-lg col-span-2">
                <p className="font-bold underline text-end">
                  Top {penjualan.top?.payment?.length} Payment Method
                </p>
                <div className="flex flex-wrap gap-3 mt-2 justify-center">
                  <ReactApexChart
                    options={options2}
                    labels={options2.labels}
                    series={options2.series.data}
                    type="donut"
                    height={450}
                    width={"100%"}
                  />
                </div>
              </div>
            </div>

            {penjualan && penjualan !== undefined ? (
              <div className="mt-5">
                {penjualan.top && penjualan.top !== undefined ? (
                  <div className="flex">
                    <div className="p-3 rounded-md border border-black w-[35%]">
                      <p className="text-blue-500 text-center font-bold">
                        Top {penjualan.top.product.length} Products
                      </p>
                      <div className="border border-black my-3"></div>
                      {penjualan.top.product.map((data, index) => (
                        <p className="mt-3 font-bold" key={index}>
                          {index + 1}. {data.name} - {data.total} pcs
                        </p>
                      ))}
                    </div>
                    <div className="p-3 rounded-md border border-black w-[35%] mx-3">
                      <p className="text-blue-500 text-center font-bold">
                        Top {penjualan.top.value.length} Products by Transaction
                      </p>
                      <div className="border border-black my-3"></div>
                      {penjualan.top.value.map((data, index) => (
                        <p className="mt-3 font-bold" key={index}>
                          {index + 1}. {data.name} -
                          {data.total.toLocaleString("id-ID", {
                            style: "currency",
                            currency: "IDR",
                          })}
                        </p>
                      ))}
                    </div>
                    <div className="p-3 rounded-md border border-black w-[35%]">
                      <p className="text-blue-500 text-center font-bold">
                        Top {penjualan.top.category.length} Categories
                      </p>
                      <div className="border border-black my-3"></div>
                      {penjualan.top.category.map((data, index) => (
                        <p className="mt-3 font-bold" key={index}>
                          {index + 1}. {data.name} - {data.total} pcs
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Graph recharts*/}

            {reportingTable && reportingTable.data_table !== null ? (
              <>
                <div className="mt-5 w-full">
                  <div className="flex flex-row justify-between items-center">
                    <p>Grafik Penjualan</p>
                    <button
                      onClick={handleDownload}
                      className="p-2 px-5 bg-blue-500 text-white rounded-lg"
                    >
                      Download Chart
                    </button>
                  </div>
                  <div className="relative m-auto h-[50vh] w-[60vw]">
                    <Line data={datas} options={options} id="a" />
                  </div>
                </div>
                <div>
                  <div className="flex flex-row justify-between mt-5">
                    <p className="font-bold" id="drop 1">
                      Laporan Penjualan
                    </p>
                    {/* <CSVLink data={dataCSV} headers={headerCSV} target="_blank"></CSVLink> */}
                    <button
                      className="p-2 px-5 bg-blue-500 text-white rounded-lg"
                      onClick={() =>
                        mutateExportCsv({
                          method,
                          period,
                          start_date: startDate ? toApiDate(startDate) : "",
                          end_date: endDate ? toApiDate(endDate) : "",
                        })
                      }
                    >
                      Export
                    </button>
                  </div>
                  <div>
                    {isReportingTableLoading ? (
                      <div className="flex items-center justify-center my-28">
                        <CircularLoading />
                      </div>
                    ) : (
                      <>
                        <TableContainer sx={{ marginTop: 3 }} component={Paper}>
                          <Table
                            sx={{ width: "100%" }}
                            aria-label="simple-table"
                          >
                            <TableHead className="text-[10px]">
                              <TableRow
                                sx={{
                                  backgroundColor: "#007AF1",
                                }}
                              >
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Invoice Date
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Order ID
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Nama Produk
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Quantity
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Kemasan
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Total Pembelian
                                </TableCell>
                                <TableCell
                                  align="center"
                                  onClick={() => handleSort("quantity")}
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  <p className="flex flex-row justify-center items-center gap-2">
                                    Payment/Order Method Quantity
                                    {sortBy.column.includes("quantity") &&
                                    sortBy.order === "asc" ? (
                                      <FaSortUp className="text-[10px] mt-1 font-semibold text-blue-500" />
                                    ) : sortBy.column.includes("quantity") &&
                                      sortBy.order === "desc" ? (
                                      <FaSortUp className="rotate-180 text-[10px] mb-1 font-semibold text-blue-500" />
                                    ) : (
                                      <FaSort className="text-[10px] text-blue-500" />
                                    )}
                                  </p>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Selling Method
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Sales
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Notes/Catatan
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  Status
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  B2B Id
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ color: "white", fontWeight: "bold" }}
                                >
                                  B2C Id
                                </TableCell>
                                {/*<TableCell
                                    align="center"
                                    onClick={() => handleSort("total")}
                                    sx={{ color: "white", fontWeight: "bold" }}
                                  >
                                    <p className="flex flex-row justify-center items-center gap-2">
                                      Kasir / Apoteker
                                      {sortBy.column.includes("total") &&
                                      sortBy.order === "asc" ? (
                                        <FaSortUp className="text-[10px] mt-1 font-semibold text-blue-500" />
                                      ) : sortBy.column.includes("total") &&
                                        sortBy.order === "desc" ? (
                                        <FaSortUp className="rotate-180 text-[10px] mb-1 font-semibold text-blue-500" />
                                      ) : (
                                        <FaSort className="text-[10px] text-blue-500" />
                                      )}
                                    </p>
                                  </TableCell>
                                */}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {reportingTable && !isReportingTableLoading ? (
                                reportingTable.data_table.map((data, index) => (
                                  <TableRow
                                    key={index}
                                    sx={{
                                      "&:last-child td, &:last-child th": {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      className="whitespace-nowrap"
                                    >
                                      {toDate(data.invoice_date)}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.order_id}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.name_medicine}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.quantity}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.uom}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.total.toLocaleString("id-ID", {
                                        style: "currency",
                                        currency: "IDR",
                                      })}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.payment_method}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.selling_method}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.sales_name}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.notes.length === 0
                                        ? "-"
                                        : data.notes}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.status}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.b2b_id.length === 0
                                        ? "-"
                                        : data.b2b_id}
                                    </TableCell>
                                    <TableCell align="center">
                                      {data.b2c_id === 0 ? "-" : data.b2c_id}
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell
                                    colSpan={13}
                                    scope="row"
                                    className="whitespace-nowrap"
                                  >
                                    {isReportingTableLoading ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      "Tidak ada data"
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <div className="flex justify-center mt-[10px]">
                          <PagePagination
                            offset={offset}
                            setOffset={setOffset}
                            setOffsetMemory={(limit, offset) => {
                              navigateTo(
                                offset,
                                limit,
                                startDate
                                  ? moment(startDate).format("YYYY-MM-DD")
                                  : "",
                                endDate
                                  ? moment(endDate).format("YYYY-MM-DD")
                                  : "",
                                period,
                                method,
                              );
                            }}
                            limit={fetchLimit}
                            setLimit={(e) => {
                              setFetchLimit(e);
                              navigateTo(
                                1,
                                e,
                                startDate
                                  ? moment(startDate).format("YYYY-MM-DD")
                                  : "",
                                endDate
                                  ? moment(endDate).format("YYYY-MM-DD")
                                  : "",
                                period,
                                method,
                              );
                            }}
                            total={reportingTable.count}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex justify-center w-full mt-5">
                <p>tidak ada produk</p>
              </div>
            )}
          </>
        ) : (
          <div className="w-full flex items-center justify-center mt-5">
            <p className="txt-[25px] font-bold">
              tidak ada transaksi yang berlangsung
            </p>
          </div>
        )}
      </>

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />

      <Dialog open={date} onClose={() => setDate(false)}>
        <DialogTitle>Pilih Tanggal</DialogTitle>
        <DialogContent>
          <div className="flex">
            <DatePicker
              selected={startDate}
              onChange={(dates) => {
                if (!dates) return;
                const [start, end] = dates || [];
                setStartDate(start ? new Date(start) : null);
                setEndDate(end ? new Date(end) : null);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange={true}
              inline
            />
          </div>
          <button
            className="bg-indigo-500 rounded text-white px-2 py-1 font-semibold w-full"
            onClick={() => {
              setPeriodDummy(10);
              setDate(false);
              navigateTo(
                offset,
                fetchLimit,
                startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                endDate ? 10 : 8,
                method,
              );
            }}
          >
            Konfirmasi
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Penjualan;
